import React from "react";
import { Link } from "react-router-dom";
import { X, Minus, Plus } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { useCart } from "../context/CartContext";
import { formatINR } from "../lib/api";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal } = useCart();

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        className="bg-[#c3ba9e] border-l border-[#a89e80] w-full sm:max-w-md flex flex-col p-0"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#a89e80]">
          <SheetTitle className="font-serif-display text-2xl flex items-center justify-between">
            <span>Your Cart</span>
            <span className="eyebrow">{items.length} {items.length === 1 ? "piece" : "pieces"}</span>
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-10 text-center">
              <p className="font-serif-display text-2xl text-[#1A1A1A]">Your cart is quiet.</p>
              <p className="text-sm text-[#7A7A7A] mt-2">
                Begin with a signature piece from our festive edit.
              </p>
              <Link to="/shop" onClick={() => setIsOpen(false)}>
                <Button className="mt-8 bg-[#1A1A1A] hover:bg-[#000000] text-[#c3ba9e] rounded-none px-8 py-6 tracking-[0.2em] uppercase text-xs btn-shimmer">
                  Explore the Shop
                </Button>
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-[#a89e80]">
              {items.map((item) => (
                <li key={item.key} className="p-6 flex gap-4" data-testid={`cart-item-${item.id}`}>
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-24 object-cover bg-[#b8af92]"
                  />
                  <div className="flex-1">
                    <div className="flex justify-between gap-3">
                      <div>
                        <p className="font-serif-display text-lg leading-tight">{item.name}</p>
                        <p className="text-xs text-[#7A7A7A] mt-1 tracking-wider">
                          {item.color} · Size {item.size}
                        </p>
                      </div>
                      <button
                        onClick={() => removeItem(item.key)}
                        aria-label="Remove"
                        className="text-[#7A7A7A] hover:text-[#1A1A1A]"
                        data-testid={`cart-remove-${item.id}`}
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-[#a89e80]">
                        <button
                          onClick={() => updateQty(item.key, item.qty - 1)}
                          className="px-2 py-1.5 hover:bg-[#b8af92]"
                          aria-label="Decrease"
                          data-testid={`cart-qty-down-${item.id}`}
                        >
                          <Minus size={12} />
                        </button>
                        <span className="px-3 text-sm w-8 text-center">{item.qty}</span>
                        <button
                          onClick={() => updateQty(item.key, item.qty + 1)}
                          className="px-2 py-1.5 hover:bg-[#b8af92]"
                          aria-label="Increase"
                          data-testid={`cart-qty-up-${item.id}`}
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <p className="text-sm font-body">{formatINR(item.price * item.qty)}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[#a89e80] p-6 space-y-4 bg-[#c3ba9e]">
            <div className="flex items-center justify-between">
              <span className="eyebrow">Subtotal</span>
              <span className="font-serif-display text-2xl" data-testid="cart-subtotal">
                {formatINR(subtotal)}
              </span>
            </div>
            <p className="text-[11px] text-[#7A7A7A] tracking-wider">
              Taxes & shipping calculated at checkout. Complimentary shipping across India.
            </p>
            <Link to="/cart" onClick={() => setIsOpen(false)} className="block">
              <Button
                className="w-full bg-[#1A1A1A] hover:bg-[#000000] text-[#c3ba9e] rounded-none py-6 tracking-[0.22em] uppercase text-xs btn-shimmer"
                data-testid="cart-view-full"
              >
                View Cart & Checkout
              </Button>
            </Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
